import React from "react";
import { applyRotationToPolygon } from "./overlap1"; 
import { API_URL } from "../../../config";
import { FloorPlanData, Room, Point } from "./types";
import { getValidAccessToken } from "../../../Utility/authUtils";

const API_BASE_URL = API_URL;


function rotatePoint(point: Point, center: { x: number, z: number }, angleDegrees: number): Point {
  const angleRadians = (angleDegrees * Math.PI) / 180;
  
  const translatedX = point.x - center.x;
  const translatedZ = point.z - center.z;

  const rotatedX = translatedX * Math.cos(angleRadians) - translatedZ * Math.sin(angleRadians);
  const rotatedZ = translatedX * Math.sin(angleRadians) + translatedZ * Math.cos(angleRadians);
  
  return {
    x: rotatedX + center.x,
    z: rotatedZ + center.z
  };
}

function applyRoomRotations(
  floorPlanData: FloorPlanData, 
  roomRotations: { [key: string]: number }
): FloorPlanData {
  const updatedRooms = floorPlanData.rooms.map(room => {
    const rotation = roomRotations[room.id] || 0;
    
    if (rotation === 0) {
      return room;
    }

    const rotatedPolygon = applyRotationToPolygon(room.floor_polygon, rotation);
    
    return {
      ...room,
      floor_polygon: rotatedPolygon
    };
  });
  
  return {
    ...floorPlanData,
    rooms: updatedRooms
  };
}

async function updatePlanCoordinates(
  projectId: string,
  planId: string,
  floorPlanData: FloorPlanData
): Promise<{status : boolean, data : any}> {
  try {
    const token = await getValidAccessToken();
    if (!token) {
      console.error('No access token found');
      return {status : false, data : null};
    }

    const coordinatesPayload = {
      coordinates: floorPlanData
    };

    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/plans/${planId}/`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(coordinatesPayload)
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Failed to update plan:', errorData);
      return {status : false, data : null};
    }

    const updatedPlan = await response.json();
    return {status : true, data : updatedPlan};

  } catch (error) {
    console.error('Error updating plan coordinates:', error);
    return {status : false, data : null};
  }
}

// Helper function to ensure currentPlan is always updated
const updateCurrentPlanInStorage = (projectId: string, planId: string, floorPlanData: FloorPlanData) => {
    try {
        const planKey = `${projectId}/${planId}`;
        const cachedCurrentPlan = localStorage.getItem("currentPlan");
        const currentPlans = cachedCurrentPlan ? JSON.parse(cachedCurrentPlan) : {};
        
        // Always update or create the plan entry
        currentPlans[planKey] = {
            id: parseInt(planId),
            project: parseInt(projectId),
            plan_name: currentPlans[planKey]?.plan_name || `Plan ${planId}`,
            coordinates: floorPlanData,
            objects: floorPlanData.objects || []
        };
        
        localStorage.setItem("currentPlan", JSON.stringify(currentPlans));
        console.log('Updated/created currentPlan in localStorage');
    } catch (error) {
        console.error('Error updating currentPlan:', error);
    }
};

export async function saveFloorPlan(
  floorPlanData: FloorPlanData, 
  roomRotations: { [key: string]: number },
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
  projectId: string,
  planId: string
) {
  try {
    const finalFloorPlanData = applyRoomRotations(floorPlanData, roomRotations);
  
    const updateSuccess = await updatePlanCoordinates(projectId, planId, finalFloorPlanData);
    // console.log("updateSuccess", updateSuccess);
    
    if (!updateSuccess.status) {
      alert("Failed to save floor plan to server");
      return floorPlanData;
    }
    
    const cachedProjects = localStorage.getItem("userProjects");

    
    if (cachedProjects) {
      const projects = JSON.parse(cachedProjects);
      const updatedProjects = projects.map((project: any) => 
        project.id.toString() === projectId 
          ? {
              ...project,
              plans: project.plans.map((plan: any) => 
                plan.id.toString() === planId 
                  ? { ...plan, coordinates: floorPlanData, history : updateSuccess.data.history }
                  : plan
              )
            }
          : project
      );
      localStorage.setItem("userProjects", JSON.stringify(updatedProjects));
    }

    // ALWAYS update currentPlan as a fallback
    updateCurrentPlanInStorage(projectId, planId, floorPlanData);
    
    //console.log(JSON.stringify(finalFloorPlanData));
    
    setHasChanges(false);
    
    // Return the data with the new history
    return {
      ...finalFloorPlanData,
      history: updateSuccess.data.history
    };
  } catch (error) {
    console.error("Error saving floor plan:", error);
    alert("Failed to save floor plan");
    return floorPlanData;
  }
}